import { useState, type FormEvent } from 'react';
import type { MeResponse } from '@whaletail/shared';
import { ApiError, registerAccount, signIn } from '../api.ts';

interface Props {
  registrationOpen: boolean;
  onAuthed: (me: MeResponse) => void;
}

const PASSWORD_MIN = 8;

/**
 * The whole app sits behind this — there is no anonymous view of the map
 * (see App.tsx's early return). This is a full page, not a dismissible
 * <dialog>: there is nothing to dismiss to, since the app itself doesn't
 * render until someone is signed in. (Supersedes the old AuthDialog, which
 * this pulled its form logic from.)
 */
export function LoginGate({ registrationOpen, onAuthed }: Props) {
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const switchMode = (next: 'signin' | 'register') => {
    setMode(next);
    setError(null);
    setPassword('');
    setConfirm('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'register') {
      if (password.length < PASSWORD_MIN) return setError(`Use at least ${PASSWORD_MIN} characters for your password.`);
      if (password !== confirm) return setError("Those passwords don't match.");
    }

    setBusy(true);
    try {
      const me = mode === 'register' ? await registerAccount(username.trim(), password) : await signIn(username.trim(), password);
      setPassword('');
      setConfirm('');
      onAuthed(me);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const registering = mode === 'register';

  return (
    <div className="gate">
      <form onSubmit={submit} className="gate-card">
        <picture className="brand gate-brand">
          <source srcSet="/whaletail-vertical-dark.png" media="(prefers-color-scheme: dark)" />
          {/* width/height are the real intrinsic pixel size (both variants match),
              so the browser reserves the right aspect ratio before the image
              loads; CSS (width: 100%; height: auto) is what actually sizes it —
              full card width, matching the "login bubble" — see accounts.css. */}
          <img src="/whaletail-vertical.png" alt="WhaleTail" width={1988} height={1760} />
        </picture>

        <label className="field">
          <span>Username</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            minLength={3}
            maxLength={32}
            autoFocus
          />
          {registering && <small>3–32 letters, numbers, dots, dashes or underscores.</small>}
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={registering ? 'new-password' : 'current-password'}
            required
            maxLength={200}
          />
          {registering && <small>At least {PASSWORD_MIN} characters. A few random words works well.</small>}
        </label>

        {registering && (
          <label className="field">
            <span>Confirm password</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              maxLength={200}
            />
          </label>
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn btn-primary btn-block gate-submit" disabled={busy}>
          {busy ? 'Please wait…' : registering ? 'Create Account' : 'Sign In'}
        </button>

        {/* Below the button, not a second control competing with it above the
            form: only appears at all when self-serve registration is open,
            which it normally is not — an admin creates accounts instead. */}
        {registrationOpen &&
          (registering ? (
            <p className="dialog-switch">
              Already have an account?{' '}
              <button type="button" className="link-btn" onClick={() => switchMode('signin')}>
                Sign In
              </button>
            </p>
          ) : (
            <p className="dialog-switch">
              New here?{' '}
              <button type="button" className="link-btn" onClick={() => switchMode('register')}>
                Create an Account
              </button>
            </p>
          ))}

        {registering && (
          <p className="dialog-fineprint">
            There is no email on file, so a forgotten password can only be reset by the person who runs this server.
          </p>
        )}
      </form>
    </div>
  );
}
